// NewProjectWizardPage — redesigned 7-step project creation wizard
// (Tuttify Carbon look). Lives alongside the original SmartIntakePage
// at /intake/v2; it reuses the exact same API pipeline so a project
// created here is indistinguishable from one created via /intake.
//
// Step order (product decision — see plan): Describe → Team → Delivery
// → Compliance → Security → Infrastructure → Shape. Security and Team
// are UI-only: they render and persist to the local draft but have no
// backend field, so they are never sent to /intake.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '@/lib/api'
import { useUxStore } from '@/stores/uxStore'
import {
  formatRelativeAge,
} from '@/stores/intakeDraftStore'
import { useNewProjectDraftStore } from '@/stores/newProjectDraftStore'
import {
  clearPendingFiles,
  loadPendingFiles,
  savePendingFiles,
} from '@/lib/pendingFileStore'
import {
  collapseAudience,
  detectNouns,
  detectRegulatory,
  detectShape,
  getShape,
} from '@/components/intake/shapes'
import type { RegulatoryFlag } from '@/types'

import type { NewProjectForm } from './types'
import './new-project-wizard.css'
import { WizardNav } from './WizardNav'
import { ProgressRail } from './ProgressRail'
import { StepActions } from './StepActions'
import { StepDescribe } from './steps/StepDescribe'
import { StepTeam } from './steps/StepTeam'
import { StepDelivery } from './steps/StepDelivery'
import { StepCompliance } from './steps/StepCompliance'
import { StepSecurity } from './steps/StepSecurity'
import { StepInfra } from './steps/StepInfra'
import { StepShape } from './steps/StepShape'

function initialForm(): NewProjectForm {
  return {
    name: '',
    problem_statement: '',
    shape: null,
    vertical: null,
    regulatory: [],
    regulatoryUserEdited: false,
    audience: ['engineers'],
    cadence: 'standard',
    document_owner: '',
    document_approver: '',
    ai_governance_disposition: null,
    ai_governance_ratified_by: '',
    ai_governance_rationale: '',
    cloud_provider: '',
    primary_region: '',
    container_runtime: '',
    orchestrator: '',
    iac_framework: '',
    secrets_store: '',
    secrets_rotation_policy: '',
    oncall_primary_rotation: '',
    oncall_runbook_index: '',
    oncall_pager_severity_thresholds: '',
    securityControls: [],
    infraSelections: [],
    teamAssignments: {},
    projectMembers: [],
    contentFiles: [],
    formatFiles: [],
  }
}

const STEPS = [
  { name: 'Describe', sub: 'Problem + context' },
  { name: 'Team', sub: 'Assigned units' },
  { name: 'Delivery', sub: 'Audience & cadence' },
  { name: 'Compliance', sub: 'Regulatory cues' },
  { name: 'Security', sub: 'Posture & controls' },
  { name: 'Infrastructure', sub: 'Stack & runtime' },
  { name: 'Shape', sub: 'Solution archetype' },
]

export function NewProjectWizardPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setProjectShape = useUxStore((s) => s.setShape)
  const setProjectAudience = useUxStore((s) => s.setAudience)
  const setProjectVertical = useUxStore((s) => s.setVertical)

  const persistedDraft = useNewProjectDraftStore((s) => s.draft)
  const saveDraft = useNewProjectDraftStore((s) => s.saveDraft)
  const clearDraft = useNewProjectDraftStore((s) => s.clearDraft)

  const [step, setStep] = useState(1)
  const [form, setForm] = useState<NewProjectForm>(initialForm)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [draftPrompt, setDraftPrompt] = useState<'pending' | 'resumed' | 'fresh'>(() =>
    persistedDraft && (persistedDraft.name.trim() || persistedDraft.problem_statement.trim())
      ? 'pending'
      : 'fresh',
  )

  const update = (patch: Partial<NewProjectForm>) => setForm((f) => ({ ...f, ...patch }))

  const detected = useMemo(
    () => ({
      shape: detectShape(form.problem_statement),
      regulatory: detectRegulatory(form.problem_statement),
      nouns: detectNouns(form.problem_statement),
    }),
    [form.problem_statement],
  )

  // Pre-select the detected shape if the user hasn't picked one.
  useEffect(() => {
    if (!form.shape && detected.shape.shape) {
      setForm((f) => ({ ...f, shape: detected.shape.shape!.id }))
    }
  }, [detected.shape.shape, form.shape])

  // Surface auto-detected regulatory cues until the user edits the list.
  useEffect(() => {
    if (form.regulatoryUserEdited) return
    const detectedFlags = detected.regulatory.map((r) => r.flag)
    setForm((f) => {
      const same =
        f.regulatory.length === detectedFlags.length &&
        f.regulatory.every((flag) => detectedFlags.includes(flag))
      if (same) return f
      return { ...f, regulatory: detectedFlags }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detected.regulatory.map((r) => r.flag).join(','), form.regulatoryUserEdited])

  const buildDraft = () => ({
    name: form.name,
    problem_statement: form.problem_statement,
    shape: form.shape,
    vertical: form.vertical,
    regulatory: form.regulatory,
    regulatoryUserEdited: form.regulatoryUserEdited,
    audience: form.audience,
    cadence: form.cadence,
    document_owner: form.document_owner,
    document_approver: form.document_approver,
    ai_governance_disposition: form.ai_governance_disposition,
    ai_governance_ratified_by: form.ai_governance_ratified_by,
    ai_governance_rationale: form.ai_governance_rationale,
    cloud_provider: form.cloud_provider,
    primary_region: form.primary_region,
    container_runtime: form.container_runtime,
    orchestrator: form.orchestrator,
    iac_framework: form.iac_framework,
    secrets_store: form.secrets_store,
    secrets_rotation_policy: form.secrets_rotation_policy,
    oncall_primary_rotation: form.oncall_primary_rotation,
    oncall_runbook_index: form.oncall_runbook_index,
    oncall_pager_severity_thresholds: form.oncall_pager_severity_thresholds,
    securityControls: form.securityControls,
    infraSelections: form.infraSelections,
    teamAssignments: form.teamAssignments,
    projectMembers: form.projectMembers,
    step,
  })

  // Debounced auto-save.
  useEffect(() => {
    if (draftPrompt === 'pending') return
    const handle = window.setTimeout(() => {
      const meaningful = form.name.trim().length > 0 || form.problem_statement.trim().length > 0
      if (meaningful) saveDraft(buildDraft())
      // File objects can't live in the localStorage draft, so mirror the
      // queued uploads to IndexedDB under the same slot. Without this a
      // reload / resume silently drops attached docs (they never reach
      // Phase 2). Runs independently of `meaningful` so a file attached
      // before any text is typed still survives.
      void savePendingFiles('new_project', {
        contentFiles: form.contentFiles,
        formatFiles: form.formatFiles,
      })
    }, 600)
    return () => window.clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, step, draftPrompt, saveDraft])

  const resumeDraft = () => {
    if (!persistedDraft) {
      setDraftPrompt('fresh')
      return
    }
    setForm((f) => ({
      ...f,
      name: persistedDraft.name,
      problem_statement: persistedDraft.problem_statement,
      shape: persistedDraft.shape,
      vertical: persistedDraft.vertical,
      regulatory: persistedDraft.regulatory,
      regulatoryUserEdited: persistedDraft.regulatoryUserEdited,
      audience: persistedDraft.audience.length > 0 ? persistedDraft.audience : f.audience,
      cadence: persistedDraft.cadence,
      document_owner: persistedDraft.document_owner ?? '',
      document_approver: persistedDraft.document_approver ?? '',
      ai_governance_disposition: persistedDraft.ai_governance_disposition ?? null,
      ai_governance_ratified_by: persistedDraft.ai_governance_ratified_by ?? '',
      ai_governance_rationale: persistedDraft.ai_governance_rationale ?? '',
      cloud_provider: persistedDraft.cloud_provider ?? '',
      primary_region: persistedDraft.primary_region ?? '',
      container_runtime: persistedDraft.container_runtime ?? '',
      orchestrator: persistedDraft.orchestrator ?? '',
      iac_framework: persistedDraft.iac_framework ?? '',
      secrets_store: persistedDraft.secrets_store ?? '',
      secrets_rotation_policy: persistedDraft.secrets_rotation_policy ?? '',
      oncall_primary_rotation: persistedDraft.oncall_primary_rotation ?? '',
      oncall_runbook_index: persistedDraft.oncall_runbook_index ?? '',
      oncall_pager_severity_thresholds: persistedDraft.oncall_pager_severity_thresholds ?? '',
      securityControls: persistedDraft.securityControls ?? [],
      infraSelections: persistedDraft.infraSelections ?? [],
      teamAssignments: persistedDraft.teamAssignments ?? {},
      projectMembers: persistedDraft.projectMembers ?? [],
    }))
    setStep(persistedDraft.step >= 1 && persistedDraft.step <= STEPS.length ? persistedDraft.step : 1)
    // Restore queued uploads from IndexedDB alongside the text fields.
    void loadPendingFiles('new_project').then((files) => {
      setForm((f) => ({
        ...f,
        contentFiles: files.contentFiles,
        formatFiles: files.formatFiles,
      }))
    })
    setDraftPrompt('resumed')
  }

  const discardDraft = () => {
    clearDraft()
    void clearPendingFiles('new_project')
    setDraftPrompt('fresh')
  }

  const saveAndExit = () => {
    saveDraft(buildDraft())
    void savePendingFiles('new_project', {
      contentFiles: form.contentFiles,
      formatFiles: form.formatFiles,
    })
    navigate('/projects')
  }

  // Deployment catalog (Tier 1) — feeds the Delivery "Project
  // Architecture" selects. Tolerates a not-yet-chosen shape (this flow
  // collects shape on the LAST step); the endpoint accepts shape: null.
  const regKey = form.regulatory.join(',')
  const { data: deploymentCatalog } = useQuery({
    queryKey: ['deploymentCatalog', form.shape, regKey],
    queryFn: () => api.getDeploymentCatalog({ shape: form.shape, regulatory: form.regulatory }),
    staleTime: 5 * 60 * 1000,
  })

  // Submit flow (fired when the user hits "Generate Requirements" on the last
  // step). This mutation makes the backend calls in a fixed order — a
  // newcomer should know all four:
  //   1. POST /api/projects           — create the empty project (via api.createProject)
  //   2. upload files                 — each queued content + format file (api.uploadInput);
  //                                      failures are collected, not fatal (project still exists)
  //   3. POST /api/projects/{id}/intake — send the intake payload (api.submitIntake): shape,
  //                                      audience, cadence, compliance/AI-governance + deployment fields
  //   4. create the Phase-1 briefing  — api.createBriefing(id, 1, 1); idempotent, so failure is swallowed
  // On success we clear the saved draft + queued files and navigate to the
  // new project page. Validation guards below run BEFORE any network call.
  const submit = useMutation({
    mutationFn: async () => {
      setSubmitError(null)
      if (!form.shape) throw new Error('Pick a project shape before submitting.')
      if (form.audience.length === 0) throw new Error('Select at least one audience.')
      const hasComplianceFlag = form.regulatory.some((r) => r && r !== 'none')
      if (hasComplianceFlag) {
        if (!form.document_owner.trim()) {
          throw new Error(
            'Document owner is required for compliance-flagged projects. Fill it on the Delivery step (Document control) or remove the compliance flag.',
          )
        }
        if (!form.document_approver.trim()) {
          throw new Error(
            'Document approver is required for compliance-flagged projects. Fill it on the Delivery step (Document control) or remove the compliance flag.',
          )
        }
      }
      if (form.ai_governance_disposition && !form.ai_governance_ratified_by.trim()) {
        throw new Error(
          'AI governance ratifier name is required when a disposition is picked. Fill it on the Compliance step or clear the disposition.',
        )
      }

      const project = await api.createProject({
        name: form.name.trim(),
        problem_statement: form.problem_statement.trim(),
      })

      const uploadFailures: string[] = []
      for (const pf of form.contentFiles) {
        try {
          await api.uploadInput(project.id, 'content', pf.file)
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : (err as Error).message
          uploadFailures.push(`content "${pf.file.name}" — ${msg}`)
        }
      }
      for (const pf of form.formatFiles) {
        try {
          await api.uploadInput(project.id, 'format', pf.file)
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : (err as Error).message
          uploadFailures.push(`format "${pf.file.name}" — ${msg}`)
        }
      }
      if (uploadFailures.length > 0) {
        setSubmitError(
          `Project created, but ${uploadFailures.length} upload(s) failed: ${uploadFailures
            .slice(0, 3)
            .join(' · ')}${uploadFailures.length > 3 ? ' …' : ''}`,
        )
      }

      const shape = getShape(form.shape)
      const legacyRegulatory =
        form.regulatory.length > 0 ? form.regulatory : (['none'] as RegulatoryFlag[])
      await api.submitIntake(project.id, {
        project_id: project.id,
        role: 'solutions_architect',
        domain: shape.domain,
        domain_other: shape.domain === 'other' ? shape.label : null,
        regulatory: legacyRegulatory,
        audience: collapseAudience(form.audience),
        audience_tags: form.audience,
        intervention_budget: form.cadence,
        output_template: { mode: 'system_default' },
        shape: form.shape,
        recipe: shape.recipe,
        document_owner: form.document_owner.trim() || null,
        document_approver: form.document_approver.trim() || null,
        ai_governance_disposition: form.ai_governance_disposition,
        ai_governance_ratified_by: form.ai_governance_ratified_by.trim() || null,
        ai_governance_rationale: form.ai_governance_rationale.trim() || null,
        cloud_provider: form.cloud_provider.trim() || null,
        primary_region: form.primary_region.trim() || null,
        container_runtime: form.container_runtime.trim() || null,
        orchestrator: form.orchestrator.trim() || null,
        iac_framework: form.iac_framework.trim() || null,
        secrets_store: form.secrets_store.trim() || null,
        secrets_rotation_policy: form.secrets_rotation_policy.trim() || null,
        oncall_primary_rotation: form.oncall_primary_rotation.trim() || null,
        oncall_runbook_index: form.oncall_runbook_index.trim() || null,
        oncall_pager_severity_thresholds: form.oncall_pager_severity_thresholds.trim() || null,
      })

      setProjectShape(project.id, form.shape)
      setProjectAudience(project.id, form.audience)
      setProjectVertical(project.id, form.vertical)

      try {
        await api.createBriefing(project.id, 1, 1)
      } catch {
        // Idempotent on backend.
      }

      return project
    },
    onSuccess: (project) => {
      clearDraft()
      void clearPendingFiles('new_project')
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate(`/projects/${project.id}`)
    },
    onError: (err) => {
      setSubmitError(err instanceof ApiError ? err.message : (err as Error).message)
    },
  })

  const canContinue = (() => {
    if (step === 1) return form.name.trim().length >= 2 && form.problem_statement.trim().length >= 30
    if (step === 3) return form.audience.length > 0
    if (step === 7) return form.shape !== null
    return true
  })()

  const isLast = step === STEPS.length

  const handleNext = () => {
    if (isLast) {
      submit.mutate()
    } else {
      setStep((s) => Math.min(STEPS.length, s + 1))
      window.scrollTo({ top: 0 })
    }
  }
  const handleBack = step > 1 ? () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0 }) } : null

  const showResume =
    draftPrompt === 'pending' &&
    persistedDraft &&
    (persistedDraft.name.trim().length > 0 || persistedDraft.problem_statement.trim().length > 0)

  return (
    <div className="npw-root">
      <WizardNav />
      <main className="wizard-page">
        {showResume && (
          <div className="resume-banner">
            <div className="resume-text">
              You have an unfinished project draft{' '}
              <b>{persistedDraft.name.trim() || 'Untitled'}</b> — saved{' '}
              {formatRelativeAge(persistedDraft.savedAt)}.
            </div>
            <div className="resume-actions">
              <button type="button" className="btn btn-ghost btn-wizard" onClick={discardDraft}>
                Discard
              </button>
              <button type="button" className="btn btn-molten btn-wizard" onClick={resumeDraft}>
                Resume
              </button>
            </div>
          </div>
        )}

        <ProgressRail steps={STEPS} current={step} onJump={setStep} />

        {submitError && <div className="submit-error">{submitError}</div>}

        {step === 1 && <StepDescribe form={form} update={update} detected={detected} />}
        {step === 2 && <StepTeam form={form} update={update} />}
        {step === 3 && (
          <StepDelivery
            form={form}
            update={update}
            catalog={deploymentCatalog ?? null}
          />
        )}
        {step === 4 && <StepCompliance form={form} update={update} detected={detected} />}
        {step === 5 && <StepSecurity form={form} update={update} />}
        {step === 6 && <StepInfra form={form} update={update} detected={detected} />}
        {step === 7 && <StepShape form={form} update={update} detected={detected} />}

        <StepActions
          onBack={handleBack}
          onSaveDraft={saveAndExit}
          onNext={handleNext}
          isLast={isLast}
          canContinue={canContinue}
          submitting={submit.isPending}
        />
      </main>
    </div>
  )
}
